import { browserHistory } from 'react-router';

import { get } from 'lodash';
import { call, put, select } from 'redux-saga/effects';
import { LOGGER_DATA_LOCAL_STORAGE_KEY, LoggerStatusEnum, sendLog } from 'ui-model';

import { changeLocale, initApp, signOut } from '@lib/actions';
import { selectCurrentMaster, selectCurrentUser } from '@lib/selectors';
import { trackUser } from '@lib/services/eventsService';
import storageService from '@lib/services/storageService';
import { HOST_VARIANT_KEY } from '@lib/utils/getHostVariant';

import { snackbarError } from 'actions/snackbarMessage';
import { closeWebNotification, getBroadcastsCount, registerSocket } from 'containers/App/actions';

import sagaFetchCurrentUser from './sagaFetchCurrentUser';
import sagaValidateToken from './sagaValidateToken';

function* logoutActions() {
  yield put(signOut({ includeReturnUrl: true }));

  // app ready
  yield put(initApp.success({}));
}

export default function* sagaInitApp(action: ReturnType<typeof initApp.request>) {
  const {
    payload: { location, meta },
  } = action;

  // set the host variant in session storage
  const hostVariant = location?.query?.hostVariant as string | undefined;
  if (hostVariant) sessionStorage.setItem(HOST_VARIANT_KEY, hostVariant);

  // validate token / session
  const token = get(location, 'query.token') || storageService.getToken();
  let validationData;
  let isReferringProvider = false;
  let isPatient = false;

  if (token) {
    // @ts-ignore
    validationData = yield sagaValidateToken({ token });
    const userId = validationData?.data?.iss;
    // TODO rigoni-roger replace to removeCalendarStateLocalStorage fn from ui-common
    localStorage.removeItem(`lh:calendar-settings:${userId}`);
    localStorage.removeItem(`lh:calendar-selected-providers:${userId}`);

    const userRoles = get(validationData, 'data.roles', []);
    isReferringProvider = userRoles.length === 1 && userRoles[0] === 'referringProvider';
    isPatient = userRoles.length === 1 && userRoles[0] === 'patient';
  }

  // if validationData === undefined, validationData.error sends a TypeError
  if (!token || Boolean(get(validationData, 'error', '')) || isReferringProvider || isPatient) {
    yield logoutActions();

    return;
  }

  storageService.setUser({ token });

  // fetch current user
  try {
    yield call(sagaFetchCurrentUser);
  } catch (error) {
    sendLog('Error on sagaInitApp.ts calling sagaFetchCurrentUser.js', { error }, LoggerStatusEnum.error);
    yield logoutActions();
    yield put(snackbarError('Something went wrong. Please try logging in again.'));
    return { error };
  }

  // register socket + load initial data
  // TODO: normalize
  // @ts-ignore
  const currentUser = yield select(selectCurrentUser);
  // TEMP FIX: Providers getting session expired after TH call.
  storageService.setUser(currentUser);
  window.localStorage.setItem(
    LOGGER_DATA_LOCAL_STORAGE_KEY,
    JSON.stringify({ userName: get(currentUser, 'name', ''), userId: get(currentUser, 'id', '') }),
  );

  // prevent calls when user is invalid. Invalid user has token = undefined
  if (!get(currentUser, 'token', false)) {
    logoutActions();

    return;
  }

  const { _id, master, name } = currentUser;

  yield put(registerSocket({ _id }));

  yield put(getBroadcastsCount());
  yield put(closeWebNotification());

  // update locale
  const locale = get(currentUser, 'settings.localization');
  yield put(changeLocale(locale));

  // Track user for GTM
  currentUser.masterAccountName = name;
  if (!master) {
    // @ts-ignore
    const currentMaster = yield select(selectCurrentMaster);
    currentUser.masterAccountName = currentMaster.name;
  }
  trackUser(currentUser);

  // app ready
  yield put(initApp.success({}));

  // callbackSuccess
  if (meta && meta.callbackSuccess) {
    if (window.location.pathname === '/login' && currentUser?.impersonate?.length) {
      browserHistory.push('/admin');
      return null;
    }
    meta.callbackSuccess(validationData);
  }
}
